import React, { useState, useEffect } from 'react';
import { adminApiClient } from '../lib/api';
import ImageUpload from './common/ImageUpload';
import { DataTable } from './common/DataTable'; // Import DataTable
import { showDeleteConfirmation } from '../lib/sweetAlert'; // Import standard alert
import {
  Search,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  XCircle,
  Upload,
  Download,
} from 'lucide-react';
import toast from 'react-hot-toast'; // Import Toast
import { ColumnDef } from '@tanstack/react-table';

interface Pooja {
  _id: string;
  id?: string;
  name: string;
  description: string;
  temple: string | { _id: string, name: string }; // Handle populated or ID
  duration: number;
  price: number;
  currency: string;
  category: string;
  isActive: boolean;
  isPopular?: boolean;
  images?: string[];
  createdAt: string;
  deity?: string;
  type?: string;
  atTemplePrice?: number;
  atHomePrice?: number;
  requiredItemsPdf?: string;
}

interface Temple {
  _id: string;
  name: string;
}

const PoojasPage: React.FC = () => {
  const [poojas, setPoojas] = useState<Pooja[]>([]);
  const [temples, setTemples] = useState<Temple[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPooja, setEditingPooja] = useState<Pooja | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Bulk operation states
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importTempleId, setImportTempleId] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    temple: '',
    duration: 60,
    price: 0,
    currency: 'USD',
    category: 'daily',
    deity: '',
    type: 'both' as 'online' | 'offline' | 'both',
    locationType: 'both' as 'temple' | 'home' | 'both',
    atTemplePrice: 0,
    atHomePrice: 0,
    isActive: true,
    images: [] as string[],
    newImages: [] as File[],
    requiredItemsPdf: null as string | null,
    newRequiredItemsPdf: null as File | null
  });

  useEffect(() => {
    fetchPoojas();
    fetchTemples();
  }, []);

  const fetchPoojas = async () => {
    try {
      setLoading(true);
      const response = await adminApiClient.getPoojas();

      if (response.success && response.data) {
        setPoojas(response.data.poojas.map((p: any) => ({ ...p, id: p._id })));
      } else {
        toast.error(response.message || 'Failed to fetch poojas');
      }
    } catch (err) {
      toast.error('Error fetching poojas');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemples = async () => {
    try {
      const response = await adminApiClient.getTemples();
      if (response.success && response.data) {
        setTemples(response.data.temples);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      temple: '',
      duration: 60,
      price: 0,
      currency: 'USD',
      category: 'daily',
      deity: '',
      type: 'both' as 'online' | 'offline' | 'both',
      locationType: 'both' as 'temple' | 'home' | 'both',
      atTemplePrice: 0,
      atHomePrice: 0,
      isActive: true,
      images: [],
      newImages: [],
      requiredItemsPdf: null,
      newRequiredItemsPdf: null
    });
    setEditingPooja(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const loadingToast = toast.loading(editingPooja ? 'Updating pooja...' : 'Creating pooja...');

    try {
      // Form Data preparation
      let submitData: any;
      const hasImages = formData.newImages && formData.newImages.length > 0;
      const hasPdf = !!formData.newRequiredItemsPdf;

      if (hasImages || hasPdf) {
        submitData = new FormData();
        submitData.append('name', formData.name);
        submitData.append('description', formData.description);
        submitData.append('duration', formData.duration.toString());
        submitData.append('price', formData.price.toString());
        submitData.append('currency', formData.currency);
        submitData.append('category', formData.category);
        submitData.append('isActive', formData.isActive.toString());
        if (hasImages) {
          formData.newImages.forEach((file) => submitData.append('images', file));
        }
        if (hasPdf) {
          submitData.append('requiredItemsPdf', formData.newRequiredItemsPdf);
        }
      } else {
        submitData = {
          name: formData.name,
          description: formData.description,
          duration: formData.duration,
          price: formData.price,
          currency: formData.currency,
          category: formData.category,
          deity: formData.deity,
          type: formData.type,
          locationType: formData.locationType,
          atTemplePrice: formData.locationType !== 'home' ? formData.atTemplePrice : undefined,
          atHomePrice: formData.locationType !== 'temple' ? formData.atHomePrice : undefined,
          isActive: formData.isActive
        };
      }

      let response;
      if (editingPooja) {
        response = await adminApiClient.updatePooja(editingPooja._id, submitData);
      } else {
        if (!formData.temple) throw new Error('Temple is required');
        response = await adminApiClient.createPooja(formData.temple, submitData);
      }

      if (response.success) {
        toast.success(editingPooja ? 'Pooja updated' : 'Pooja created', { id: loadingToast });
        setShowForm(false);
        resetForm();
        fetchPoojas();
      } else {
        toast.error(response.message || 'Operation failed', { id: loadingToast });
      }
    } catch (err: any) {
      toast.error(err.message || 'Error saving pooja', { id: loadingToast });
    }
  };

  const handleEdit = (pooja: Pooja) => {
    setEditingPooja(pooja);
    setFormData({
      name: pooja.name,
      description: pooja.description,
      temple: typeof pooja.temple === 'object' ? pooja.temple?._id : pooja.temple,
      duration: pooja.duration,
      price: pooja.price,
      currency: pooja.currency,
      category: pooja.category,
      deity: pooja.deity || '',
      type: (pooja.type as 'online' | 'offline' | 'both') || 'both',
      locationType: (pooja as any).locationType || 'both',
      atTemplePrice: pooja.atTemplePrice || 0,
      atHomePrice: pooja.atHomePrice || 0,
      isActive: pooja.isActive,
      images: pooja.images || [],
      newImages: [],
      requiredItemsPdf: pooja.requiredItemsPdf || null,
      newRequiredItemsPdf: null
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const isConfirmed = await showDeleteConfirmation('Delete Pooja?', 'This action cannot be undone.');
    if (!isConfirmed) return;

    try {
      const response = await adminApiClient.deletePooja(id);
      if (response.success) {
        toast.success('Pooja deleted');
        fetchPoojas();
      } else {
        toast.error('Failed to delete pooja');
      }
    } catch (err) {
      toast.error('Error deleting pooja');
    }
  };

  const handleImageDelete = async (imageUrl: string) => {
    if (!editingPooja) return;
    try {
      await adminApiClient.deletePoojaImage(editingPooja._id, imageUrl);
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleTogglePopular = async (poojaId: string, isPopular: boolean) => {
    try {
      const response = await adminApiClient.updatePooja(poojaId, { isPopular });
      if (response.success) {
        toast.success(`Pooja marked as ${isPopular ? 'Popular' : 'Standard'}`);
        // Optimistically update local state or fetch
        setPoojas(prev => prev.map(p => p._id === poojaId ? { ...p, isPopular } : p));
      } else {
        toast.error(response.message || 'Failed to update status');
        // Revert on failure if we did optimistic update, but here we just show error
      }
    } catch (error) {
      toast.error('Failed to update popular status');
    }
  };

  const handleBulkDelete = async () => {
    const isConfirmed = await showDeleteConfirmation('Delete Selected Poojas?', `This will permanently delete ${selectedIds.size} poojas.`);
    if (!isConfirmed) return;

    const loadToast = toast.loading('Deleting...');
    try {
      const response = await adminApiClient.bulkDeletePoojas(Array.from(selectedIds));
      if (response.success) {
        toast.success(response.message || 'Poojas deleted', { id: loadToast });
        setSelectedIds(new Set());
        fetchPoojas();
      } else {
        toast.error(response.message || 'Failed to delete poojas', { id: loadToast });
      }
    } catch (err) {
      toast.error('Error deleting poojas', { id: loadToast });
    }
  };

  const downloadSampleCSV = () => {
    const headers = ['name', 'description', 'deity', 'duration', 'atTemplePrice', 'atHomePrice', 'currency', 'category', 'locationType', 'type', 'isActive', 'isPopular'];
    const rows = [
      "Daily Pooja (Both locations),A daily pooja performed at both locations,,,50,150,USD,,both,both,true,true",
      "Special Temple Pooja,A special pooja performed strictly at the temple,,,100,0,INR,,temple,offline,true,false",
      "Festival Home Pooja,A festival pooja performed at the devotee's home,,,0,200,USD,,home,offline,true,true",
      "Weekly Temple Pooja,A weekly pooja that is currently inactive,,,30,0,CAD,,temple,both,false,false",
      "Monthly Online Pooja,A monthly pooja offered online,,,20,50,GBP,,both,online,true,false"
    ];
    const csvContent = "data:text/csv;charset=utf-8," + headers.join(',') + "\n" + rows.join('\n') + "\n";
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "poojas_sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCSV = (content: string) => {
    const lines = content.split('\n');
    const rawHeaders = lines[0].split(',').map(h => h.trim());
    
    // Header mapping for common truncated names
    const headerMap: { [key: string]: string } = {
      'atTempleP': 'atTemplePrice',
      'atTemplePri': 'atTemplePrice',
      'atHomePri': 'atHomePrice',
      'atHomePrice': 'atHomePrice',
      'locationTy': 'locationType',
      'isPopula': 'isPopular'
    };

    const headers = rawHeaders.map(h => headerMap[h] || h);

    const result = [];
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      const currentline = lines[i].split(',');
      const obj: any = {};
      for (let j = 0; j < headers.length; j++) {
        let val: any = currentline[j]?.trim();
        if (val === undefined || val === '') continue; // Skip empty fields to use defaults

        const lowerVal = val.toLowerCase();
        if (lowerVal === 'true') val = true;
        else if (lowerVal === 'false') val = false;
        else if (!isNaN(Number(val))) val = Number(val);

        obj[headers[j]] = val;
      }
      if (Object.keys(obj).length > 0) {
        result.push(obj);
      }
    }
    return result;
  };

  const handleBulkImport = async () => {
    if (!importFile || !importTempleId) {
      toast.error('Please select a temple and a file');
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const text = e.target?.result as string;
        let dataToImport = [];
        if (importFile.name.endsWith('.json')) {
          dataToImport = JSON.parse(text);
        } else if (importFile.name.endsWith('.csv')) {
          dataToImport = parseCSV(text);
        } else {
          toast.error('Unsupported file format. Please use .csv or .json');
          return;
        }

        const loadingToast = toast.loading('Importing poojas...');
        const res = await adminApiClient.bulkImportPoojas(importTempleId, dataToImport);
        if (res.success) {
          const message = res.message || `Imported ${res.data?.importedCount || 0} poojas successfully.`;
          const errors = res.data?.errors || [];
          
          if (errors.length > 0) {
            toast.success(`${message} But had ${errors.length} errors.`, { id: loadingToast, duration: 5000 });
            console.error('Import errors:', errors);
          } else {
            toast.success(message, { id: loadingToast });
          }
          
          setShowImportModal(false);
          setImportFile(null);
          setImportTempleId('');
          fetchPoojas();
        } else {
          toast.error(res.message || 'Import failed', { id: loadingToast });
        }
      } catch (err: any) {
        toast.error('Error parsing file. Check format.', { id: 'import' });
      }
    };
    reader.readAsText(importFile);
  };

  // Columns for DataTable
  const columns: ColumnDef<Pooja>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <label className="flex items-center justify-center p-2">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-[var(--color-primary)] focus:ring-[var(--color-primary)] cursor-pointer"
            checked={table.getRowModel().rows.length > 0 && selectedIds.size === table.getRowModel().rows.length}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedIds(new Set(table.getRowModel().rows.map(r => r.original._id)));
              } else {
                setSelectedIds(new Set());
              }
            }}
          />
        </label>
      ),
      cell: ({ row }) => (
        <label className="flex items-center justify-center p-2">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-[var(--color-primary)] focus:ring-[var(--color-primary)] cursor-pointer"
            checked={selectedIds.has(row.original._id)}
            onChange={(e) => {
              const newSelected = new Set(selectedIds);
              if (e.target.checked) newSelected.add(row.original._id);
              else newSelected.delete(row.original._id);
              setSelectedIds(newSelected);
            }}
          />
        </label>
      )
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info) => (
        <div>
          <div className="font-medium text-gray-900">{info.getValue() as string}</div>
          <div className="text-xs text-gray-500 line-clamp-1">{info.row.original.description}</div>
        </div>
      )
    },
    {
      accessorKey: 'temple',
      header: 'Temple',
      cell: (info) => {
        const val = info.getValue() as any;
        return val?.name || temples.find(t => t._id === val)?.name || 'Unknown';
      }
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: (info) => (
        <span className="font-medium text-gray-700">
          {info.row.original.currency} {info.getValue() as number}
        </span>
      )
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: (info) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[color-mix(in_srgb,var(--color-secondary)_10%,white)] text-[var(--color-secondary)] capitalize">
          {info.getValue() as string || '-'}
        </span>
      )
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: (info) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${info.getValue() ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {info.getValue() ? 'Active' : 'Inactive'}
        </span>
      )

    },
    {
      id: 'isPopular',
      header: 'Popular',
      cell: (info) => {
        const isPopular = !!info.row.original.isPopular;
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={isPopular}
              onChange={(e) => handleTogglePopular(info.row.original._id, e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[color-mix(in_srgb,var(--color-primary)_30%,white)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[var(--color-primary)]"></div>
          </label>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (info) => (
        <div className="flex items-center gap-2">
          <button onClick={() => handleEdit(info.row.original)} className="p-1.5 text-gray-500 hover:text-[var(--color-secondary)] hover:bg-[color-mix(in_srgb,var(--color-secondary)_10%,white)] rounded-lg">
            <Edit2 size={16} />
          </button>
          <button onClick={() => handleDelete(info.row.original._id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg">
            <Trash2 size={16} />
          </button>
        </div>
      )
    }
  ];

  const filteredPoojas = poojas.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Poojas Management</h1>
          <p className="text-sm text-gray-500">Manage temple poojas and services</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition shadow-sm"
            >
              <Trash2 size={18} /> Delete Selected ({selectedIds.size})
            </button>
          )}
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition shadow-sm"
          >
            <Upload size={18} /> Bulk Import
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 transition shadow-sm"
          >
            <Plus size={18} /> Add Pooja
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search poojas..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)]"
          />
        </div>
        <button onClick={fetchPoojas} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          data={filteredPoojas}
          columns={columns}
          pagination={undefined}
          sorting={undefined}
          enableClientSidePagination={true}
          loading={loading}
        />
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl animate-in fade-in zoom-in duration-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">{editingPooja ? 'Edit Pooja' : 'New Pooja'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:bg-gray-100 p-1 rounded-full"><XCircle size={24} /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Temple</label>
                  <select required value={formData.temple} onChange={e => setFormData({ ...formData, temple: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] outline-none">
                    <option value="">Select Temple</option>
                    {temples.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] outline-none"></textarea>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deity</label>
                  <input type="text" value={formData.deity} onChange={e => setFormData({ ...formData, deity: e.target.value })} placeholder="e.g. Lord Ganesha" className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] outline-none" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
                  <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} value={formData.duration} onChange={e => setFormData({ ...formData, duration: parseInt(e.target.value) })} className="w-full px-3 py-2 border rounded-lg outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select value={formData.currency} onChange={e => setFormData({ ...formData, currency: e.target.value })} className="w-full px-3 py-2 border rounded-lg outline-none">
                    <option value="USD">USD</option>
                    <option value="INR">INR</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className="w-full px-3 py-2 border rounded-lg outline-none">
                    <option value="daily">Daily</option>
                    <option value="special">Special</option>
                    <option value="festival">Festival</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location Type</label>
                  <select value={formData.locationType} onChange={e => setFormData({ ...formData, locationType: e.target.value as 'temple' | 'home' | 'both' })} className="w-full px-3 py-2 border rounded-lg outline-none">
                    <option value="both">Both (Temple & Home)</option>
                    <option value="temple">Temple Only</option>
                    <option value="home">Home Only</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {(formData.locationType === 'both' || formData.locationType === 'temple') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">At Temple Price</label>
                    <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} min="0" value={formData.atTemplePrice} onChange={e => setFormData({ ...formData, atTemplePrice: parseFloat(e.target.value) })} className="w-full px-3 py-2 border rounded-lg outline-none" />
                  </div>
                )}
                {(formData.locationType === 'both' || formData.locationType === 'home') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">At Home Price</label>
                    <input type="number" onWheel={(e) => (e.target as HTMLElement).blur()} min="0" value={formData.atHomePrice} onChange={e => setFormData({ ...formData, atHomePrice: parseFloat(e.target.value) })} className="w-full px-3 py-2 border rounded-lg outline-none" />
                  </div>
                )}
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} className="w-4 h-4 text-[var(--color-primary)] rounded" />
                  <span className="text-sm font-medium text-gray-700">Active Status</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
                <ImageUpload
                  images={formData.images}
                  newImages={formData.newImages}
                  onImagesChange={imgs => setFormData({ ...formData, images: imgs })}
                  onNewImagesChange={files => setFormData({ ...formData, newImages: files })}
                  onImageDelete={handleImageDelete}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Required Items PDF (Optional)</label>
                <div className="flex items-center gap-4">
                  <label className="cursor-pointer bg-white px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                    <Upload size={16} />
                    {formData.newRequiredItemsPdf ? 'Change PDF' : formData.requiredItemsPdf ? 'Update Existing PDF' : 'Upload PDF'}
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && file.type === 'application/pdf') {
                          setFormData({ ...formData, newRequiredItemsPdf: file });
                        } else if (file) {
                          toast.error('Please upload a valid PDF file.');
                        }
                      }}
                    />
                  </label>
                  {formData.newRequiredItemsPdf ? (
                    <span className="text-sm text-gray-600 truncate max-w-xs bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] px-3 py-1 rounded-md text-[color-mix(in_srgb,var(--color-primary)_80%,black)] font-medium">
                      New: {formData.newRequiredItemsPdf.name}
                    </span>
                  ) : formData.requiredItemsPdf ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-green-700 font-medium px-3 py-1 bg-green-50 rounded-md">
                        Existing PDF Uploaded
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">No PDF selected</span>
                  )}
                  {(formData.newRequiredItemsPdf || formData.requiredItemsPdf) && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, newRequiredItemsPdf: null, requiredItemsPdf: null })}
                      className="text-red-500 hover:text-red-700 text-sm font-medium ml-auto"
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
                <button type="submit" className="px-4 py-2 text-white bg-[var(--color-primary)] hover:opacity-90 rounded-lg">
                  {editingPooja ? 'Save Changes' : 'Create Pooja'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md animate-in fade-in zoom-in duration-200 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Bulk Import Poojas</h2>
              <button onClick={() => { setShowImportModal(false); setImportFile(null); }} className="text-gray-400 hover:bg-gray-100 p-1 rounded-full"><XCircle size={24} /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Temple <span className="text-red-500">*</span></label>
                <select required value={importTempleId} onChange={e => setImportTempleId(e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] outline-none">
                  <option value="">Select Temple</option>
                  {temples.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Select the temple where these poojas will be imported to.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Data File (.csv, .json) <span className="text-red-500">*</span></label>
                <input
                  type="file"
                  accept=".csv,.json"
                  onChange={e => setImportFile(e.target.files ? e.target.files[0] : null)}
                  className="w-full px-3 py-2 border border-gray-200 border-dashed rounded-lg bg-gray-50 file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] file:text-[color-mix(in_srgb,var(--color-primary)_80%,black)]"
                />
              </div>

              <div className="pt-2">
                <button
                  onClick={downloadSampleCSV}
                  className="flex items-center gap-1 text-sm text-[var(--color-secondary)] hover:text-[var(--color-secondary)]"
                >
                  <Download size={14} /> Download Sample CSV Format
                </button>
                <p className="text-xs text-gray-500 mt-2">
                  Only <strong>name</strong> is required. Fields like <em>deity</em>, <em>duration</em>, and <em>category</em> are optional and can be added later by editing individual poojas. Image uploads are also not supported during bulk import.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => { setShowImportModal(false); setImportFile(null); }} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancel</button>
                <button
                  onClick={handleBulkImport}
                  disabled={!importFile || !importTempleId}
                  className="px-4 py-2 text-white bg-[var(--color-primary)] hover:opacity-90 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Import Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoojasPage;
